import {memo, useMemo} from 'react'

import {useAULogInView} from '@financial/common-logs'
import classNames from 'classnames/bind'

import {CheckCoverLayer} from '$components/insurance/my/home/diagnosis/CheckCoverLayer'
import InsuranceComparison from '$components/insurance/my/home/diagnosis/InsuranceComparison'
import {InsuranceCoverageCheckSummaryText} from '$components/insurance/my/home/diagnosis/InsuranceCoverageCheckSummaryText'
import InsuranceGraph from '$components/insurance/my/home/diagnosis/InsuranceGraph'
import auBases from '$constants/auBases'
import {
    TAB_TYPE,
    또래대비_보장조건,
    또래대비_보장조건_색깔,
    실손의료_코드,
    실손의료_코드_이름,
    암뇌심_목록,
} from '$constants/insurance/diagnosis'
import {INTERNAL_ROUTES} from '$constants/internal-routes'
import {useDiagnosisCoverage} from '$hooks/insurance/my/home/diagnosis/useDiagnosisCoverage'
import useMyDataAgreementWebAppEnv from '$hooks/insurance/my/useMyDataAgreementWebAppEnv'
import {GRAPH_LABEL, 보장분류_전체, 보장분류_카테고리} from '$share/constants/insurance'
import {formatAmount} from '$utils/formatAmount'
import {get또래대비보장ratio} from '$utils/insurance'

import styles from './InsuranceEstimate.module.scss'

const cx = classNames.bind(styles)

export const REPORT_ID = 'insurance-report-id'

const checkWithUnit = (input: string) => (input === '0' ? `${input}원` : input)

const 암뇌심Nlog: Record<string, {nclick: string; aulog: string}> = {
    [GRAPH_LABEL.암]: {
        nclick: 'insumy.diagnosiscancer',
        aulog: 'insu_check_cancer',
    },
    [GRAPH_LABEL.뇌]: {
        nclick: 'insumy.diagnosisbrain',
        aulog: 'insu_check_brain',
    },
    [GRAPH_LABEL.심장]: {
        nclick: 'insumy.diagnosisheart',
        aulog: 'insu_check_heart',
    },
}

const 암뇌심비교 = memo(function 암뇌심비교() {
    const {coverages} = useDiagnosisCoverage()

    const 암뇌심 = useMemo(() => coverages.filter((elem) => 암뇌심_목록.includes(elem.labelName)), [coverages])

    const comparisonList = useMemo(() => {
        return 암뇌심.map((elem) => ({
            name: elem.labelName,
            badge: elem.myAmount === 0 ? '없음' : elem.condition,
            color: 또래대비_보장조건_색깔[elem.condition],
            rate: `${get또래대비보장ratio(elem.myAmount, elem.peerAmount)}%`,
            peer: formatAmount(elem.peerAmount, {type: '간단'}),
            amount: checkWithUnit(formatAmount(elem.myAmount, {type: '간단'})),
            href: {pathname: INTERNAL_ROUTES['INSURANCE_MY_COVERAGES_[TYPE]'], query: {type: elem.labelName}},
            nlog: 암뇌심Nlog[elem.labelName].nclick || '',
            aulog: {...auBases.insurance.homeB, code: 암뇌심Nlog[elem.labelName].aulog || ''},
        }))
    }, [암뇌심])

    const comparisonText = useMemo(() => {
        const 암뇌심_모두없음 = 암뇌심.filter((elem) => elem.myAmount === 0).length === 암뇌심.length
        if (암뇌심_모두없음) {
            return (
                <>
                    가장 흔한 사망원인, 3대 질병은 치료비 부담이 크니
                    <br />
                    목돈받는 <b>진단비를 먼저</b> 챙기는 게 좋아요.
                </>
            )
        }

        const 암뇌심_부족여부 = 암뇌심.filter((elem) => elem.condition !== 또래대비_보장조건.충분).length > 0
        if (암뇌심_부족여부) {
            return (
                <>
                    가장 흔한 사망원인, 3대 질병은 치료비 부담이 크니
                    <br />
                    목돈받는 <b>진단비를 먼저</b> 챙기는 게 좋아요.
                </>
            )
        }

        return (
            <>
                가장 흔한 사망원인, 3대 질병에 대해 <br />
                보장이 잘 준비되어 있어요.
            </>
        )
    }, [암뇌심])

    return <InsuranceComparison title="3대 주요 질병" list={comparisonList} description={comparisonText} />
})

const 실손의료비Nlog: Record<string, {nclick: string; aulog: string}> = {
    [실손의료_코드_이름[실손의료_코드.질병통원]]: {
        nclick: 'insumy.diagnosisdiseasetw',
        aulog: 'insu_check_disease_tw',
    },
    [실손의료_코드_이름[실손의료_코드.질병입원]]: {
        nclick: 'insumy.diagnosisdiseaseiw',
        aulog: 'insu_check_disease_iw',
    },
    [실손의료_코드_이름[실손의료_코드.상해통원]]: {
        nclick: 'insumy.diagnosisinjurytw',
        aulog: 'insu_check_injury_tw',
    },
    [실손의료_코드_이름[실손의료_코드.상해입원]]: {
        nclick: 'insumy.diagnosisinjuryiw',
        aulog: 'insu_check_injury_iw',
    },
}

const 실손의료비목록 = memo(function 실손의료비목록() {
    const {실손의료비, 통원보장_여부, 입원보장_여부} = useDiagnosisCoverage()

    const comparisonList = useMemo(
        () =>
            실손의료비.map((elem) => {
                const 보장카테고리 = elem.benefitName.slice(2)
                const needHref = 보장카테고리 === 보장분류_카테고리.통원 ? 통원보장_여부 : 입원보장_여부
                const href = needHref
                    ? {
                          pathname: INTERNAL_ROUTES['INSURANCE_MY_COVERAGES_[TYPE]'],
                          query: {type: 보장분류_전체, category: 보장카테고리},
                      }
                    : ''

                return {
                    name: elem.benefitName.slice(0, 2),
                    way: 보장카테고리,
                    amount: elem.benefitAmount === 0 ? '없음' : formatAmount(elem.benefitAmount, {type: '간단'}),
                    color:
                        elem.benefitAmount === 0
                            ? 또래대비_보장조건_색깔[또래대비_보장조건.매우부족]
                            : 또래대비_보장조건_색깔[또래대비_보장조건.충분],
                    href,
                    nlog: 실손의료비Nlog[elem.benefitName].nclick || '',
                    aulog: {...auBases.insurance.homeB, code: 실손의료비Nlog[elem.benefitName].aulog || ''},
                }
            }),
        [실손의료비, 입원보장_여부, 통원보장_여부],
    )

    const comparisonText = useMemo(() => {
        const 가입목록 = 실손의료비.filter((elem) => elem.benefitAmount > 0)

        // 실손의료비 가입 안함
        if (가입목록.length === 0) {
            return (
                <>
                    실손보험은 <b>의료비를 지원해주는 필수 보험</b>이에요.
                    <br />꼭 가입해서 병원비 지출을 줄여 보세요.
                </>
            )
        }

        // 4개 모두 가입함
        if (가입목록.length === 실손의료비.length) {
            return <>실손보험이 빠짐없이 잘 준비되어 있어요.</>
        }

        // 1~3개
        return (
            <>
                실손보험은 모든 보장에 가입해야 안전해요.
                <br />
                없는 보장을 채워 보세요.
            </>
        )
    }, [실손의료비])

    return (
        <InsuranceComparison title="실손 의료비" list={comparisonList} description={comparisonText} typeSingle={true} />
    )
})

// 보장점검
export const InsuranceCoverageCheck = memo(function InsuranceCoverageCheck() {
    const shouldShowInfo = useMyDataAgreementWebAppEnv()

    const ref = useAULogInView({...auBases.insurance.homeP, code: 'insu_check_coverages'})

    return (
        <div ref={ref} className={cx('tab-content')}>
            <h4 className="blind">보장 진단</h4>
            <div className={cx('report')} id={REPORT_ID}>
                <InsuranceGraph shouldShowInfo={!!shouldShowInfo} />
                {!shouldShowInfo && <CheckCoverLayer tabType={TAB_TYPE.coverage} />}
            </div>
            {!!shouldShowInfo && (
                <>
                    <InsuranceCoverageCheckSummaryText />
                    <암뇌심비교 />
                    <실손의료비목록 />
                </>
            )}
        </div>
    )
})
